import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Params } from '@angular/router';
import {
  Pair,
  TopOfBookSource,
  TopOfBookSourceControllerService,
  TopOfBookSourceRequest,
  TopOfBookSourceUpdateRequest,
} from '@api/core';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withFeature,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { withUrlFilter } from '@shared/store/url/with-url-filter';
import { asUndefinedForEmptyString } from '@shared/utils/url-params';
import { isDefined } from '@stillmandigital/common';
import { UpdateTopOfBookSourceDialogComponent } from 'app/settings/components/top-of-book/update-top-of-book-source-dialog/update-top-of-book-source-dialog.component';
import { Dictionary, keyBy } from 'lodash';
import { pipe, switchMap, take } from 'rxjs';

export const TOP_OF_BOOK_ROUTE_ID = 'topOfBookRouteId';

export interface TopOfBookFilter {
  type?: TopOfBookSourceUpdateRequest.TypeEnum;
  pairName?: string;
}

export interface TopOfBookDetailsResponse {
  topOfBooks: TopOfBookDetails[];
}

export interface TopOfBookDetails {
  sourceId: number;
  midPrice: number;
  askPrice?: number;
  askSize?: number;
  bidPrice?: number;
  bidSize?: number;
  dataTimestamp: string;
  sentTimestamp: string;
}

export interface TopOfBookSourceViewModel extends Omit<TopOfBookSource, 'source1' | 'source2' | 'source3' | 'source4'> {
  source1?: TopOfBookSource;
  source2?: TopOfBookSource;
  source3?: TopOfBookSource;
  source4?: TopOfBookSource;
  midPrice?: number;
  lpId?: number;
}

export const typesWithSources: TopOfBookSource.TypeEnum[] = [
  TopOfBookSource.TypeEnum.Aggregate,
  TopOfBookSource.TypeEnum.Multiply,
  TopOfBookSource.TypeEnum.Divide,
  TopOfBookSource.TypeEnum.PerpSpotChaining,
];

export interface TopOfBookFormModel {
  type?: TopOfBookSource.TypeEnum;
  name?: string;
  pair?: Pair;
  enabled?: boolean;
  source1?: TopOfBookSource;
  source2?: TopOfBookSource;
  source3?: TopOfBookSource;
  source4?: TopOfBookSource;
  weight1?: number;
  weight2?: number;
  weight3?: number;
  weight4?: number;
  lpId?: number;
}

export const useTopOfBook = () =>
  signalStoreFeature(
    {
      props: type<{
        _notificationService: NotificationService;
        pairsMap: Signal<Dictionary<Pair>>;
      }>(),
    },
    withState({
      topOfBookSources: new Array<TopOfBookSource>(),
      topOfBookSourcesLoaded: false,
      topOfBookDetails: new Array<TopOfBookDetails>(),
      pageFilter: {} as TopOfBookFilter,
    }),
    withProps(() => ({
      _topOfBookService: inject(TopOfBookSourceControllerService),
      _dialogService: inject(MatDialog),
    })),
    withMethods(() => {
      const _urlParamsToPageFilter = (params: Params): TopOfBookFilter => ({
        type: params.type,
        pairName: params.pairName,
      });

      const _pageFilterToUrlParams = (pageFilter: TopOfBookFilter): Params => ({
        type: pageFilter.type,
        pairName: asUndefinedForEmptyString(pageFilter.pairName),
      });

      return { _urlParamsToPageFilter, _pageFilterToUrlParams };
    }),
    withFeature(() => withUrlFilter<TopOfBookFilter>(TOP_OF_BOOK_ROUTE_ID)),
    withComputed(store => {
      const topOfBookSourcesMap = computed(() => keyBy(store.topOfBookSources(), 'id'));

      const topOfBookSourcesOptions = computed(() =>
        store.topOfBookSources().map(source => ({ id: source.id!, name: source.name! })),
      );

      const topOfBookDetailsMap = computed(() => keyBy(store.topOfBookDetails(), 'sourceId'));

      const topOfBookDetailsRequest = computed(() => ({
        sourceIds: store
          .topOfBookSources()
          .filter(model => model.enabled && isDefined(model.id))
          .map(model => model.id)
          .sort(),
      }));

      const topOfBookViewModels = computed(() => {
        const sources = store.topOfBookSources();
        const sourcesMap = topOfBookSourcesMap();
        const detailsMap = topOfBookDetailsMap();
        const pageFilter = store.pageFilter();
        const pMap = store.pairsMap();

        return sources
          .filter(model => (isDefined(pageFilter.type) ? model.type === pageFilter.type : true))
          .map(model => ({
            ...model,
            midPrice: detailsMap[model.id!]?.midPrice,
            lpId: model.pair?.liquidityProviderId,
            source1: isDefined(model.source1) ? sourcesMap[model.source1] : undefined,
            source2: isDefined(model.source2) ? sourcesMap[model.source2] : undefined,
            source3: isDefined(model.source3) ? sourcesMap[model.source3] : undefined,
            source4: isDefined(model.source4) ? sourcesMap[model.source4] : undefined,
            pair: isDefined(model.pairId) ? pMap[model.pairId] : undefined,
          }))
          .filter(model =>
            (model.pair?.name || '').toLowerCase().includes(pageFilter.pairName?.trim().toLowerCase() || ''),
          );
      });

      return {
        topOfBookSourcesMap,
        topOfBookSourcesOptions,
        topOfBookDetailsMap,
        topOfBookDetailsRequest,
        topOfBookViewModels,
      };
    }),
    withMethods(store => {
      const loadTopOfBookSources = rxMethod<void>(
        pipe(
          switchMap(() =>
            store._topOfBookService.findTopOfBookSources({}).pipe(
              tapResponse({
                next: topOfBookSources => patchState(store, { topOfBookSources, topOfBookSourcesLoaded: true }),
                error: (error: HttpErrorResponse) => {
                  store._notificationService.showErrorNotification(`The top of book sources couldn't be loaded`, error);
                  patchState(store, { topOfBookSourcesLoaded: true });
                },
              }),
            ),
          ),
        ),
      );

      const updateTopOfBookDetails = (details: TopOfBookDetails[]) => {
        patchState(store, { topOfBookDetails: details });
      };

      const updateTopOfBookPageFilter = (pageFilter: TopOfBookFilter) => {
        patchState(store, { pageFilter: pageFilter });
      };

      const openCreateTopOfBookSourceDialog = () => {
        store._dialogService.open(UpdateTopOfBookSourceDialogComponent, {
          data: {
            id: undefined,
            type: undefined,
            enabled: true,
          },
        });
      };

      const openEditTopOfBookSourceDialog = (source: TopOfBookSourceViewModel) => {
        store._dialogService.open(UpdateTopOfBookSourceDialogComponent, {
          data: source,
        });
      };

      const createTopOfBookSource = (request: TopOfBookSourceRequest) => {
        store._topOfBookService
          .createTopOfBookSource({ topOfBookSourceRequest: request })
          .pipe(take(1))
          .subscribe({
            next: () => {
              store._notificationService.showSuccessNotification('The source has been created successfully');
              store._dialogService.closeAll();
              loadTopOfBookSources();
            },
            error: (error: HttpErrorResponse) =>
              store._notificationService.showErrorNotification(`The source couldn't be created`, error),
          });
      };

      const updateTopOfBookSource = (request: TopOfBookSourceUpdateRequest) => {
        store._topOfBookService
          .updateTopOfBookSource({ topOfBookSourceUpdateRequest: request })
          .pipe(take(1))
          .subscribe({
            next: () => {
              store._notificationService.showSuccessNotification('The source has been updated successfully');
              store._dialogService.closeAll();
              loadTopOfBookSources();
            },
            error: (error: HttpErrorResponse) =>
              store._notificationService.showErrorNotification(`The source couldn't be updated`, error),
          });
      };

      return {
        loadTopOfBookSources,
        updateTopOfBookDetails,
        updateTopOfBookPageFilter,
        openCreateTopOfBookSourceDialog,
        openEditTopOfBookSourceDialog,
        createTopOfBookSource,
        updateTopOfBookSource,
      };
    }),
  );
